import React, { memo, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { v1 as uuidV1 } from 'uuid';
import { withTranslation } from 'react-i18next';
import vmsg from 'vmsg';
import RecorderControls from './RecorderControls';
import { showNotificationAutoDestroy } from '../ShowNotification';

const recorderVmsgAudio = new vmsg.Recorder({
  wasmURL: 'https://unpkg.com/vmsg@0.3.0/vmsg.wasm',
  shimURL: 'https://unpkg.com/wasm-polyfill.js@0.2.0/wasm-polyfill.js',
});

const AudioRecorder = ({ sendRecordAudio, t }) => {
  const wrapperRef = useRef(null);
  const MAX_RECORDER_TIME_SECONDS = 300;
  const BLOB_TYPE = 'audio/mpeg';

  const initialState = {
    recordingMinutes: 0,
    recordingSeconds: 0,
    initRecording: false,
    mediaStream: null,
    mediaRecorder: null,
    audio: null,
    durationInSeconds: 0,
  };

  const [recorderState, setRecorderState] = useState(initialState);

  const cancelRecording = async () => {
    const blob = await recorderVmsgAudio.stopRecording();
    URL.revokeObjectURL(blob);
    setRecorderState(initialState);
  };

  const generateAudioName = () => {
    return `AUD-${format(new Date(), 'ddmmyyyy')}-SB${uuidV1().slice(
      0,
      5
    )}.mp3`;
  };

  const getDurationInMilli = (durationInSeconds) => {
    return durationInSeconds * 1000;
  };

  useEffect(() => {
    let recordingInterval = null;

    if (recorderState.initRecording)
      recordingInterval = setInterval(() => {
        setRecorderState((prevState) => {
          const durationInSeconds = prevState.durationInSeconds + 1;
          if (prevState.durationInSeconds === MAX_RECORDER_TIME_SECONDS) {
            clearInterval(recordingInterval);
            return prevState;
          }
          if (
            prevState.recordingSeconds >= 0 &&
            prevState.recordingSeconds < 59
          ) {
            return {
              ...prevState,
              recordingSeconds: prevState.recordingSeconds + 1,
              durationInSeconds,
            };
          }
          if (prevState.recordingSeconds === 59) {
            return {
              ...prevState,
              recordingMinutes: prevState.recordingMinutes + 1,
              recordingSeconds: 0,
              durationInSeconds,
            };
          }

          return { ...prevState, durationInSeconds };
        });
      }, 1000);
    else clearInterval(recordingInterval);

    return () => clearInterval(recordingInterval);
  });

  const handleRecord = async () => {
    if (recorderState.initRecording) {
      const blob = await recorderVmsgAudio.stopRecording();

      const id = uuidV1();
      const file = new File([blob], generateAudioName(), {
        type: BLOB_TYPE,
      });
      const assign = Object.assign(file, {
        preview: URL.createObjectURL(blob),
        id,
        uploadType: file.type,
        fileId: id,
        fileType: file.type,
        duration: getDurationInMilli(recorderState.durationInSeconds),
      });
      setRecorderState(initialState);
      sendRecordAudio(assign);
      URL.revokeObjectURL(blob);
    } else {
      try {
        await recorderVmsgAudio.initAudio();
        await recorderVmsgAudio.initWorker();
        recorderVmsgAudio.startRecording();
        setRecorderState((prevState) => ({
          ...prevState,
          initRecording: true,
        }));
      } catch (e) {
        showNotificationAutoDestroy(
          'warning',
          t('warnings.title'),
          t('warnings.request-audio')
        );
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (recorderState.initRecording) {
          cancelRecording();
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <div className="audio-recorder-wrapper" ref={wrapperRef}>
      <RecorderControls
        recorderState={recorderState}
        startRecording={handleRecord}
        cancelRecording={cancelRecording}
        saveRecording={handleRecord}
      />
    </div>
  );
};

AudioRecorder.propTypes = {
  t: PropTypes.func.isRequired,
  sendRecordAudio: PropTypes.func.isRequired,
};

export default withTranslation('common')(memo(AudioRecorder));
